if (!customElements.get("recently-viewed-products")) {
  customElements.define(
    "recently-viewed-products",
    class RecentlyViewedProducts extends HTMLElement {
      constructor() {
        super();
        this.container = this.querySelector(".products-container");
        this.productIds = [];
      }

      connectedCallback() {
        const url = this.constructQueryUrl();
        if (!url) return;
        this.fetchData(url);
      }

      disconnectedCallback() {
        if (this.swiperInstance) {
          this.swiperInstance.destroy(true, true);
        }
      }

      fetchData(url) {
        webvista
          .fetchHtml(url)
          .then((html) => {
            this.renderHtml(html);
          })
          .catch((error) => {
            console.error("Recently viewed fetch failed", error);
            const section = this.closest(".recently-viewed-section");
            if (section) section.style.display = "none";
          });
      }

      renderHtml(html) {
        const sourceTemplate = html.querySelector("template")?.content;

        if (!sourceTemplate) {
          const section = this.closest(".recently-viewed-section");
          if (section) section.style.display = "none";
          return;
        }

        const templateWrapper = sourceTemplate.querySelector(".swiper-wrapper");

        if (!templateWrapper) {
          const section = this.closest(".recently-viewed-section");
          if (section) section.style.display = "none";
          return;
        }

        const fragment = document.createDocumentFragment();

        this.productIds.forEach((id) => {
          const item = templateWrapper
            .querySelector(`[data-product-id="${id}"]`)
            ?.cloneNode(true);

          if (item) {
            item.classList.add("swiper-slide");
            fragment.appendChild(item);
          }
        });

        if (fragment.childElementCount === 0) {
          const section = this.closest(".recently-viewed-section");
          if (section) section.style.display = "none";
          return;
        }

        templateWrapper.innerHTML = "";
        templateWrapper.appendChild(fragment);

        this.container.innerHTML = "";
        this.container.appendChild(sourceTemplate);

        const section = this.closest(".recently-viewed-section");

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const slider =
              this.container.querySelector(".recently-viewed-collection-swiper") ||
              this.container.querySelector(".products-container .swiper") ||
              this.container.querySelector(".swiper");

            if (!slider) {
              if (section) section.classList.add("is-loaded");
              return;
            }

            this.initSwiper(slider);
            this.initMobileProductSwipers();

            window.dispatchEvent(new Event("resize"));
            this.swiperInstance?.update();

            // Swiper ready hone ke baad hi section dikhao
            if (section) section.classList.add("is-loaded");

            setTimeout(() => {
              this.swiperInstance?.update();
            }, 300);
          });
        });

        this.dispatchEvent(
          new CustomEvent("recently-viewed-loaded", {
            bubbles: true,
          })
        );

        // Reveal section after content is ready
        if (section) section.style.opacity = "1";

        webvista.initLazyImages();
      }

      waitForImages(container) {
        const images = container.querySelectorAll("img");

        const promises = [...images].map((img) => {
          return new Promise((resolve) => {
            if (img.complete) {
              resolve();
            } else {
              img.onload = resolve;
              img.onerror = resolve;
            }
          });
        });

        return Promise.all(promises);
      }

      initSwiper(element) {
        if (!element || typeof Swiper === "undefined") return;

        const totalSlides = element.querySelectorAll(".swiper-slide").length;

        if (this.swiperInstance) {
          this.swiperInstance.destroy(true, true);
          this.swiperInstance = null;
        }

        if (totalSlides < 2) {
          element.classList.add("swiper-disabled");

          const wrapper = element.querySelector(".swiper-wrapper");
          wrapper.style.transform = "none";
          wrapper.style.display = "flex";
          wrapper.classList.add("single-product-view");
          return;
        }

        const section = this.closest(".recently-viewed-section");
        const nextDesktop = section.querySelector(".custom-next");
        const prevDesktop = section.querySelector(".custom-prev");
        const nextMobile = section.querySelector(".custom-next-mobile");
        const prevMobile = section.querySelector(".custom-prev-mobile");

        this.swiperInstance = new Swiper(element, {
          slidesPerView: 2,
          spaceBetween: 4,
          observer: true,
          observeParents: true,
          observeSlideChildren: true,
          preloadImages: false,
          watchSlidesProgress: true,
          updateOnWindowResize: true,
          watchOverflow: true,
          allowTouchMove: false,
          simulateTouch: false,
          navigation: {
            nextEl: window.innerWidth < 768 ? nextMobile : nextDesktop,
            prevEl: window.innerWidth < 768 ? prevMobile : prevDesktop,
          },
          breakpoints: {
            0: {
              slidesPerView: 2,
              spaceBetween: 4,
              allowTouchMove: false,
              simulateTouch: false,
            },
            768: {
              slidesPerView: 3,
              spaceBetween: 12,
              allowTouchMove: true,
              simulateTouch: true,
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 4,
              allowTouchMove: true,
              simulateTouch: true,
            },
          },
          on: {
            init() {
              this.update();
            },
            resize() {
              this.update();
            },
          },
        });
      }

      initMobileProductSwipers() {
        if (window.innerWidth > 767) return;

        const swipers = this.querySelectorAll(
          ".recently-viewed-mobile-swiper:not(.swiper-initialized)"
        );

        swipers.forEach((swiperEl) => {
          new Swiper(swiperEl, {
            slidesPerView: 1,
            nested: true,
            threshold: 8,
            preloadImages: false,
            watchSlidesProgress: true,
            watchOverflow: true,
            observer: true,
            observeParents: true,
            touchStartPreventDefault: false,
            touchMoveStopPropagation: true,
            pagination: {
              el: swiperEl.querySelector(".card-image-pagination"),
              clickable: true,
            },
          });
        });
      }

      constructQueryUrl() {
        const limit = parseInt(this.dataset.limit) || 5;
        const currentProductId = this.dataset.currentProductId;
        this.productIds =
          webvista
            .retrieveData(RECENTLY_VIEWED_KEY)
            ?.filter((id) => String(id) !== String(currentProductId))
            ?.slice(-limit)
            ?.reverse() ?? [];

        if (!this.productIds.length) {
          const section = this.closest(".recently-viewed-section");
          if (section) section.style.display = "none";
          return null;
        }

        const baseUrl = this.dataset.searchUrl;
        const query = this.productIds.map((id) => `id:${id}`).join(" OR ");

        const params = new URLSearchParams({
          q: query,
          type: "product",
          section_id: this.dataset.section,
          ose: "false",
        });

        return `${baseUrl}?${params.toString()}`;
      }
    }
  );
}